import { Box, Button, ButtonBase, Dialog, DialogProps, Stack, Typography, useTheme } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { useId, useState, type ReactNode } from 'react'
import { SquareExternalLinkOMIcon } from '@omnea-app/icons'
import { DialogCloseButton } from '../DialogCloseButton'

export type FeatureTourStep = {
  key: string
  image: string
  imagePosition?: string
  title: string
  // A string gets the tour's text style; a step that needs more composes FeatureTourStepText itself.
  body: ReactNode
  learnMore?: { href: string; label: string }
}

export type FeatureTourDialogProps = {
  open: boolean
  onClose: () => void
  steps: readonly FeatureTourStep[]
  // Already translated by the caller.
  labels: { close: string; skip: string; next: string; finish: string }
  onNext?: (props: { step: number; isLastStep: boolean }) => void
  // Replaces closing as the last step's button action, e.g. when that step records a decision.
  finish?: { onClick: () => void; disabled?: boolean }
  // False makes Skip jump to the last step instead of closing, for a tour whose last step has to be seen.
  canSkipLastStep?: boolean
  // One of the theme's dialog sizes; omitted keeps the theme default.
  maxWidth?: Exclude<DialogProps['maxWidth'], false>
}

const slide = {
  initial: { opacity: 0, x: 16 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
  transition: { duration: 0.2, ease: 'easeOut' },
}

export const FeatureTourStepText = ({ children }: { children: ReactNode }) => (
  <Typography variant="upsized-regular" component="p" color="text.muted">
    {children}
  </Typography>
)

type StepCopyProps = Pick<FeatureTourStep, 'title' | 'body'> & { titleId?: string }

const StepCopy = ({ title, body, titleId }: StepCopyProps) => (
  <Stack gap={2}>
    <Typography id={titleId} variant="display-h3">
      {title}
    </Typography>
    {typeof body === 'string' ? <FeatureTourStepText>{body}</FeatureTourStepText> : body}
  </Stack>
)

export const FeatureTourDialog = ({
  open,
  onClose,
  steps,
  labels,
  onNext,
  finish,
  canSkipLastStep = true,
  maxWidth,
}: FeatureTourDialogProps) => {
  const { breakpoints } = useTheme()
  const dialogWidth = breakpoints.values[maxWidth ?? 'sm']
  const [stepIndex, setStepIndex] = useState(0)
  const [wasOpen, setWasOpen] = useState(open)
  const titleId = useId()

  // Restart from the first slide whenever the dialog (re)opens.
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setStepIndex(0)
    }
  }

  const step = steps[stepIndex] ?? steps[0]
  const isLastStep = stepIndex === steps.length - 1

  const handleNext = () => {
    onNext?.({ step: stepIndex, isLastStep })
    if (isLastStep) {
      if (finish) finish.onClick()
      else onClose()
      return
    }
    setStepIndex((current) => current + 1)
  }

  if (!step) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      aria-labelledby={titleId}
      PaperProps={{ sx: (theme) => ({ borderRadius: theme.shape['radius-2xl'], overflow: 'hidden' }) }}
    >
      <Box sx={{ position: 'relative', height: 320, bgcolor: 'bg.surface.subtle' }}>
        <AnimatePresence mode="wait">
          <motion.div key={step.key} {...slide} style={{ height: '100%' }}>
            <Image
              src={step.image}
              alt=""
              fill
              sizes={`(max-width: ${dialogWidth}px) 100vw, ${dialogWidth}px`}
              style={{ objectFit: 'cover', objectPosition: step.imagePosition ?? 'center' }}
            />
          </motion.div>
        </AnimatePresence>
        <Box sx={{ position: 'absolute', top: (theme) => theme.spacing(2), right: (theme) => theme.spacing(2) }}>
          <DialogCloseButton onClick={onClose} ariaLabel={labels.close} />
        </Box>
      </Box>

      <Stack gap={2} p={8}>
        {/* Every step's copy shares one grid cell, the others invisible, so the cell takes the tallest
            step's height and the modal never jumps between slides whatever the copy's length. */}
        <Box sx={{ display: 'grid' }}>
          {steps.map((s) => (
            <Box key={s.key} aria-hidden sx={{ gridArea: '1 / 1', visibility: 'hidden' }}>
              <StepCopy title={s.title} body={s.body} />
            </Box>
          ))}
          <Box sx={{ gridArea: '1 / 1' }}>
            <AnimatePresence mode="wait">
              <motion.div key={step.key} {...slide}>
                <StepCopy title={step.title} body={step.body} titleId={titleId} />
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap={2} alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              {steps.map((s, index) => (
                <ButtonBase
                  key={s.key}
                  aria-label={s.title}
                  aria-current={index === stepIndex ? 'step' : undefined}
                  onClick={() => setStepIndex(index)}
                  sx={(theme) => ({
                    position: 'relative',
                    height: 8,
                    width: index === stepIndex ? 24 : 8,
                    borderRadius: theme.shape['radius-full'],
                    bgcolor: index === stepIndex ? 'bg.brand.default' : 'bg.neutral.muted',
                    transition: 'width 0.2s ease, background-color 0.2s ease',
                    // Widens the click target past the 8px dot without changing the layout.
                    '&::after': { content: '""', position: 'absolute', inset: theme.spacing(-1) },
                    '&.Mui-focusVisible': {
                      outline: `${theme.strokeWidth.strongest} solid ${theme.palette.border.focus}`,
                      outlineOffset: theme.strokeWidth.strongest,
                    },
                  })}
                />
              ))}
            </Stack>
            {step.learnMore && (
              <Button
                variant="text"
                color="secondary"
                size="small"
                href={step.learnMore.href}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<SquareExternalLinkOMIcon />}
              >
                {step.learnMore.label}
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {!isLastStep && (
              <Button variant="text" onClick={canSkipLastStep ? onClose : () => setStepIndex(steps.length - 1)}>
                {labels.skip}
              </Button>
            )}
            <Button variant="contained" onClick={handleNext} disabled={isLastStep && finish?.disabled}>
              {isLastStep ? labels.finish : labels.next}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  )
}
